<template>
  <div>
    <v-navigation-drawer
      ref="DrawerRentProtectionLegalExpensesEligibilityCheck"
      v-model="valueLocal"
      app
      right
      temporary
      stateless
      class="v-navigation-drawer--width-max drawer_eligibility_check__wrapper"
    >
      <div class="drawer-container d-flex flex-column justify-space-between">
        <div
          class="drawer-header d-flex align-center justify-space-between pa-5 rounded-lg"
        >
          <h3 class="font-28 font-weight-bold">Customise your cover</h3>
        </div>
        <div class="drawer-main flex-grow-1 pa-5">
          <h3 class="black2-color font-24 font-weight-bold">
            What level of cover would you like?
          </h3>
          <div class="pa-lg-3 pt-6">
            <BaseRadio
              v-model="coverTypeLocal.Value"
              pslStyle="image"
              :width="$vuetify.breakpoint.lgAndUp ? '186px' : '106px'"
              :height="$vuetify.breakpoint.lgAndUp ? '198px' : '120px'"
              :labelClass="$vuetify.breakpoint.lgAndUp ? 'font-24' : 'font-12'"
              className="base-radio-align-center"
              :items="setItemDataUiids('CoverType', coverTypeItems)"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'What level of cover would you like?',
                },
              }"
              :imageStyleBorderRadius="10"
              aria-label="What level of cover would you like?"
            />
          </div>

          <template v-if="!isContentsOnly">
            <BaseLabel
              class="mt-10"
              text="How many years no claims discount do you have for buildings insurance?"
              :helpText="
                $vuetify.breakpoint.lgAndUp
                  ? 'Please select your current no claims discount entitlement for buildings insurance. This can be found on their previous insurer’s policy documentation. Customers can also transfer no claims discount from a Landlords buildings insurance policy that has been cancelled within the last 2 years.'
                  : ''
              "
            />
            <BaseRadio
              :style="{
                'max-width': '100%',
              }"
              class="mt-2"
              psl-style="button"
              :rules="{
                required: {
                  required: true,
                  fieldLabel:
                    'What is your current no claims discount for Buildings?',
                },
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :width="$vuetify.breakpoint.lgAndUp ? '108' : '100'"
              :items="
                setItemDataUiids('NoClaimsBuildings', NO_CLAIMS_BUILDINGS_ITEMS)
              "
              v-model="noClaimsBuildingsLocal.Value"
              aria-label="How many years no claims discount do you have for buildings insurance?"
            />
          </template>

          <template v-if="!isBuildingsOnly">
            <BaseLabel
              class="mt-10"
              text="How many years no claims discount do you have for contents insurance?"
              :helpText="
                $vuetify.breakpoint.lgAndUp
                  ? 'Please select the customer\'s current no claims discount entitlement for contents insurance. This can be found on their previous insurer’s policy documentation. The customer can also transfer no claims discount from a Landlords buildings and contents insurance policy that has been cancelled within the last 2 years.'
                  : ''
              "
            />
            <BaseRadio
              :style="{
                'max-width': '100%',
              }"
              :rules="{
                required: {
                  required: true,
                  fieldLabel:
                    'What is your current no claims discount for Contents?',
                },
              }"
              class="mt-2"
              psl-style="button"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :width="$vuetify.breakpoint.lgAndUp ? '108' : '100'"
              :items="
                setItemDataUiids('NoClaimsContents', NO_CLAIMS_CONTENTS_ITEMS)
              "
              v-model="noClaimsContentsLocal.Value"
              aria-label="How many years no claims discount do you have for contents insurance?"
            />
          </template>
        </div>
        <div class="drawer-footer d-flex flex-column pa-5">
          <div
            v-if="showRentalError(coverTypeLocal)"
            class="bg-red-50 rounded pa-3 mb-4"
          >
            <div class="d-flex">
              <v-icon color="var(--red-500)" class="mr-2 align-self-start"
                >mdi-alert-circle</v-icon
              >
              <span
                ><strong>Sorry!</strong> We're unable to offer Buildings
                Insurance for this type of property. Please change your chosen
                cover type to Contents only.</span
              >
            </div>
          </div>
          <BaseButton
            text="APPLY CHANGES"
            height="68"
            block
            color="#FAB519"
            text-color="black--text"
            icon-color="black"
            loadingIconColor="black"
            class="font-weight-bold font-20 rounded-pill white--text"
            type="submit"
            icon="mdi-refresh"
            iconSize="30"
            :loading="isRefreshingCover"
            :disabled="showRentalError(coverTypeLocal)"
            @click="refreshCover"
            aria-label="Apply changes"
          />
          <BaseButton
            text="Cancel"
            flat
            link
            depressed
            color="black--text"
            ripple
            small
            class="font-weight-medium px-1 d-flex align-self-center font-18 mt-4"
            @click="cancelChanges()"
          />
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import {
  setItemDataUiids,
  NO_CLAIMS_BUILDINGS_ITEMS,
  NO_CLAIMS_CONTENTS_ITEMS,
} from "@/constants/component.constants.js";
import { mapGetters, mapActions, mapState } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import { dereference } from "@/common/transforms";

export default {
  props: {
    value: Boolean,
    coverTypeItems: Array,
  },
  components: {
    BaseRadio,
    BaseLabel,
    BaseButton,
  },
  data() {
    return {
      setItemDataUiids,
      valueLocal: this.value,
      NO_CLAIMS_BUILDINGS_ITEMS,
      NO_CLAIMS_CONTENTS_ITEMS,
      isRefreshingCover: false,
      coverType: null,
      coverTypeLocal: null,
      noClaimsBuildings: null,
      noClaimsContents: null,
      noClaimsBuildingsLocal: null,
      noClaimsContentsLocal: null,
      initialCoverType: null,
      initialNoClaimsBuildings: null,
      initialNoClaimsContents: null,
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    ...mapGetters(QUESTIONS_MODULE, ["showRentalError"]),
    isBuildingsOnly() {
      return this.coverTypeLocal.Value === "BuildingsOnly";
    },
    isContentsOnly() {
      return this.coverTypeLocal.Value === "ContentsOnly";
    },
  },
  watch: {
    value(val) {
      this.valueLocal = val;
    },
    valueLocal(val) {
      this.$emit("input", val);
    },
  },
  created() {
    this.coverType = this.questionMap.CoverType;
    this.coverTypeLocal = dereference(this.questionMap.CoverType);

    this.noClaimsBuildings = this.questionMap.NoClaimsBuildings;
    this.noClaimsBuildingsLocal = dereference(
      this.questionMap.NoClaimsBuildings
    );

    this.noClaimsContents = this.questionMap.NoClaimsContents;
    this.noClaimsContentsLocal = dereference(this.questionMap.NoClaimsContents);

    this.initialCoverType = this.coverTypeLocal.Value;
    this.initialNoClaimsContents = this.noClaimsContentsLocal.Value;
    this.initialNoClaimsBuildings = this.noClaimsBuildingsLocal.Value;
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["getQuotes"]),
    cancelChanges() {
      if (this.isRefreshingCover) return;
      this.valueLocal = !this.valueLocal;

      this.coverTypeLocal.Value = this.initialCoverType;
      this.noClaimsBuildingsLocal.Value = this.initialNoClaimsBuildings;
      this.noClaimsContentsLocal.Value = this.initialNoClaimsContents;
    },
    refreshCover() {
      if (this.isRefreshingCover) return;
      this.isRefreshingCover = true;

      this.coverType.Value = this.coverTypeLocal.Value;
      this.noClaimsBuildings.Value = this.noClaimsBuildingsLocal.Value;
      this.noClaimsContents.Value = this.noClaimsContentsLocal.Value;

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.isRefreshingCover = false;
          this.valueLocal = false;

          this.coverTypeLocal.Value = this.coverType.Value;
          this.initialCoverType = this.coverType.Value;

          this.noClaimsBuildingsLocal.Value = this.noClaimsBuildings.Value;
          this.initialNoClaimsBuildings = this.noClaimsBuildings.Value;

          this.noClaimsContentsLocal.Value = this.noClaimsContents.Value;
          this.initialNoClaimsContents = this.noClaimsContents.Value;
        })
        .catch((error) => {
          console.log("refreshCover error", error);
          this.isRefreshingCover = false;
          this.valueLocal = false;

          this.coverType.Value = this.initialCoverType;
          this.coverTypeLocal = dereference(this.coverType);

          this.noClaimsBuildings.Value = this.initialNoClaimsBuildings;
          this.noClaimsBuildingsLocal = dereference(this.noClaimsBuildings);

          this.noClaimsContents.Value = this.initialNoClaimsContents;
          this.noClaimsContentsLocal = dereference(this.noClaimsContents);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";
</style>
